import type { ComponentType } from 'react';
import { useLingui } from '@lingui/react/macro';
import { BrowserPhoneHeartIllustration } from '@worldcoo/ui/illustrations/static/browser-phone-heart';
import { CauseCardIllustration } from '@worldcoo/ui/illustrations/static/cause-card';
import { DonationWidgetIllustration } from '@worldcoo/ui/illustrations/static/donation-widget';
import { ImpactDistributionIllustration } from '@worldcoo/ui/illustrations/static/impact-distribution';
import { PaymentConfirmedIllustration } from '@worldcoo/ui/illustrations/static/payment-confirmed';
import { PhoneConfirmationIllustration } from '@worldcoo/ui/illustrations/static/phone-confirmation';
import { ReceiptCodeIllustration } from '@worldcoo/ui/illustrations/static/receipt-code';
import { TransactionApprovedIllustration } from '@worldcoo/ui/illustrations/static/transaction-approved';

/** `en` required, so the fallback cannot be forgotten. */
export type LocalizedSrc = Record<string, string> & { en: string };

export type SolutionCard =
	/** Each `art` SVG has its own canvas (168x182, 193x193, …), so they are NOT interchangeable in size. */
	| { kind: 'step'; art: ComponentType<{ className?: string }>; title: string; description: string }
	| { kind: 'media'; src: string; alt: string }
	| { kind: 'screen'; src: LocalizedSrc; alt: string };

/** Closed, as `?solution=` opens a tab from the footer's links (TEC-3821 B4). The first opens by default. */
export const SOLUTION_KEYS = ['stores', 'ecommerce', 'payroll', 'invoices', 'portal'] as const;

export type SolutionKey = (typeof SOLUTION_KEYS)[number];

export type Solution = {
	key: SolutionKey;
	/** The CHANNEL the donation travels through, not the product. */
	tab: string;
	description: string;
	cta?: { label: string };
	cards: SolutionCard[];
};

/** Composited from Figma's two panels at (53, 40) and (53, 188.95), flattened at 2x: redo it if one moves. */
const PORTAL_IMPACT: LocalizedSrc = {
	ca: '/redesign/portal/impact-ca.png',
	en: '/redesign/portal/impact-en.png',
	es: '/redesign/portal/impact-es.png',
	fr: '/redesign/portal/impact-fr.png',
	it: '/redesign/portal/impact-it.png',
};

/**
 * SHARP ON PURPOSE: the hand-off's blur read as a rendering fault, so ask before restoring it. 4:4:4,
 * since subsampling fringes the small blue interface text.
 */
const PORTAL_PAGE: LocalizedSrc = {
	ca: '/redesign/portal/page-ca.jpg',
	en: '/redesign/portal/page-en.jpg',
	es: '/redesign/portal/page-es.jpg',
	fr: '/redesign/portal/page-fr.jpg',
	it: '/redesign/portal/page-it.jpg',
};

/** Named after what each drawing SHOWS: half are reused across channels, and stored once. */
const ART = {
	browserHeart: DonationWidgetIllustration,
	browserHeartBranches: ImpactDistributionIllustration,
	browserPhoneHeart: BrowserPhoneHeartIllustration,
	causeCard: CauseCardIllustration,
	phoneConfirmation: PhoneConfirmationIllustration,
	receiptCode: ReceiptCodeIllustration,
	terminalAccepted: PaymentConfirmedIllustration,
	terminalPrompt: TransactionApprovedIllustration,
} as const;

/** A hook, as the `t` macro only re-renders on a language change inside a component tree. */
export function useSolutions(): Solution[] {
	const { t } = useLingui();

	// Shared by `En nómina` and `Facturas`, word for word.
	const radicalSimplicity: SolutionCard = {
		kind: 'step',
		art: ART.browserHeart,
		title: t`Radical simplicity`,
		description: t`The user decides once and becomes a recurring donor. It is the "micro-donation" that generates a macro-impact.`,
	};
	const totalTransparency: SolutionCard = {
		kind: 'step',
		art: ART.browserHeartBranches,
		title: t`Total transparency`,
		description: t`100% traceability of every cent. We automate the sending of tax certificates and the project updates that keep them motivated.`,
	};

	return [
		{
			key: 'stores',
			tab: t`Stores`,
			description: t`When paying by card, the customer adds a few cents to their purchase and donates them to a social cause. Straight from the card terminal, no friction.`,
			cta: { label: t`Access the guide` },
			cards: [
				{
					kind: 'step',
					art: ART.terminalPrompt,
					title: t`Proposal`,
					description: t`When paying by card, the terminal shows a message offering to round the purchase up for a social project.`,
				},
				{
					kind: 'step',
					art: ART.terminalAccepted,
					title: t`Acceptance`,
					description: t`The shopper presses the green button to accept and inserts or taps their card.`,
				},
				{
					kind: 'step',
					art: ART.receiptCode,
					title: t`Receipt`,
					description: t`Once the transaction is approved, a donation receipt is printed with a unique, personal code.`,
				},
				{
					kind: 'step',
					art: ART.browserPhoneHeart,
					title: t`Transparency`,
					description: t`The customer enters the code at app.worldcoo.com to see the project and download their tax certificate.`,
				},
			],
		},
		{
			key: 'ecommerce',
			tab: t`Ecommerce`,
			description: t`The donation is built into your online store's payment. One click before completing the order, without interrupting the purchase.`,
			cards: [
				{
					kind: 'step',
					art: ART.browserHeart,
					title: t`Proposal`,
					description: t`At the checkout, the option to add a fixed amount appears (0.50 €, 1 €…).`,
				},
				{
					kind: 'step',
					art: ART.causeCard,
					title: t`Information`,
					description: t`A short description and an image of the cause are shown to motivate the shopper.`,
				},
				{
					kind: 'step',
					art: ART.phoneConfirmation,
					title: t`Confirmation`,
					description: t`The donation code appears on the thank-you page and is also sent by email.`,
				},
				{
					kind: 'step',
					art: ART.browserPhoneHeart,
					title: t`Certificate`,
					description: t`The customer manages their tax certificate on their own on our platform, app.worldcoo.com.`,
				},
			],
		},
		{
			key: 'payroll',
			tab: t`On payroll`,
			description: t`Every month, employees can add a microdonation to their payslip: a round-up or a fixed amount.`,
			cards: [
				radicalSimplicity,
				{ kind: 'media', src: '/redesign/payroll-desk.jpg', alt: t`An employee going through her payslip at her desk` },
				totalTransparency,
				{ kind: 'media', src: '/redesign/payroll-team.jpg', alt: t`Colleagues taking notes around a table` },
			],
		},
		{
			key: 'invoices',
			tab: t`Invoices`,
			description: t`On their monthly phone, energy or streaming invoice, the user rounds the amount up and donates the difference to social causes.`,
			cards: [
				radicalSimplicity,
				{ kind: 'media', src: '/redesign/solution-payroll.jpg', alt: t`Someone going through their receipts` },
				totalTransparency,
				{
					kind: 'media',
					src: '/redesign/invoices-thank-you.jpg',
					alt: t`The Worldcoo app thanking a donor for their donation`,
				},
			],
		},
		{
			key: 'portal',
			tab: t`Solidarity Portal`,
			description: t`A web platform tailored to your company. Ideal for emergencies, solidarity challenges or key dates: employees and customers donate from 1 €.`,
			cards: [
				{ kind: 'media', src: '/redesign/portal-laptop.jpg', alt: t`A woman donating from her laptop` },
				{ kind: 'screen', src: PORTAL_PAGE, alt: t`A solidarity portal page` },
				{ kind: 'media', src: '/redesign/portal-team.jpg', alt: t`Colleagues talking in an office` },
				{ kind: 'screen', src: PORTAL_IMPACT, alt: t`A campaign's donation progress and impact figures` },
			],
		},
	];
}
